import { useEffect, useState } from 'react';
import { publicApi } from '../lib/api';

/**
 * Fetch editable content for a specific public page.
 * Returns a helper `t(key, fallback)` that returns the saved override
 * or falls back to the hardcoded default text.
 *
 * When the page is rendered inside an iframe (e.g. PageContentAdmin live
 * preview), it also listens for `mf_preview` postMessages and overlays the
 * (unsaved) content on top of the saved content.
 */
export const usePageContent = (pageId) => {
  const [content, setContent] = useState({});
  const [previewOverlay, setPreviewOverlay] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let mounted = true;
    publicApi
      .getPageContent(pageId)
      .then((doc) => {
        if (mounted) setContent(doc?.content || {});
      })
      .catch(() => {
        // No saved overrides → render defaults from t() fallbacks
      })
      .finally(() => {
        if (mounted) setLoaded(true);
      });
    return () => {
      mounted = false;
    };
  }, [pageId]);

  // Preview bridge — only active when this window is iframed
  useEffect(() => {
    if (window === window.top) return;
    const handler = (e) => {
      const data = e.data || {};
      if (data && data.type === 'mf_preview' && data.pageId === pageId) {
        setPreviewOverlay(data.content || {});
      }
    };
    window.addEventListener('message', handler);
    try {
      window.parent.postMessage({ type: 'mf_preview_ready', pageId }, '*');
    } catch (error) {
      // parent unavailable / cross-origin; safe to ignore in production
      console.debug('preview_ready postMessage skipped:', error);
    }
    return () => window.removeEventListener('message', handler);
  }, [pageId]);

  const merged = previewOverlay ? { ...content, ...previewOverlay } : content;

  const t = (key, fallback = '') => {
    const v = merged?.[key];
    return v && String(v).trim() ? v : fallback;
  };

  return { t, content: merged, loaded };
};
